import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Pagination } from "antd";
import { CSSProperties, ReactNode, useRef } from "react";
import {
	STREAM_PAGE_SIZES,
	StreamSort,
} from "../../../utils/reporting/reportStream";

// A column of a stream's list.
export type ReportStreamColumn<T> = {
	key: string;
	// The column's name, over the column and, for screen readers and once the
	// rows stack, on each of its cells.
	header: string;
	// Its track in the full-width grid, such as "140px".
	width: string;
	// Where the cell goes once the rows stack: on the first line (the number,
	// then the status), across the row (the main text, then a full-width
	// detail), or beside the other details.
	narrow: "number" | "status" | "main" | "detail" | "full";
	// Makes the header sort the list by this sort key.
	sortKey?: string;
	render: (row: T) => ReactNode;
};

type ReportStreamListProps<T> = {
	// Names the list for screen readers, such as "Audits".
	label: string;
	columns: readonly ReportStreamColumn<T>[];
	rows: readonly T[];
	getRowKey: (row: T) => string;
	renderRowMenu: (row: T) => ReactNode;
	sort?: StreamSort;
	onSortChange: (sort: StreamSort | undefined) => void;
	// Placeholder rows show while loading, then the rows, or the message
	// instead when there is one.
	isLoading: boolean;
	loadingText: string;
	message?: ReactNode;
	pagination: {
		page: number;
		pageSize: number;
		from: number;
		to: number;
		total: number;
		onPageChange: (page: number) => void;
		onPageSizeChange: (pageSize: number) => void;
	};
};

// How wide each placeholder bar is, so the rows don't look ruled.
const PLACEHOLDER_WIDTHS = [82, 64, 90, 58, 74];

// A click on a column's header sorts up, then down, then back to the
// server's order, as the old tables' sorters did.
function getNextSort(sort: StreamSort | undefined, key: string) {
	if (sort?.key !== key) return { key, descending: false };
	if (!sort.descending) return { key, descending: true };
	return undefined;
}

function SortButton({
	header,
	sortKey,
	sort,
	onSortChange,
}: Readonly<{
	header: string;
	sortKey: string;
	sort?: StreamSort;
	onSortChange: (sort: StreamSort | undefined) => void;
}>) {
	const isActive = sort?.key === sortKey;
	let state = "not sorted";
	if (isActive)
		state = sort.descending ? "sorted descending" : "sorted ascending";
	return (
		<button
			type="button"
			className={
				isActive
					? "report-stream__sort report-stream__sort--active"
					: "report-stream__sort"
			}
			onClick={() => onSortChange(getNextSort(sort, sortKey))}
		>
			{header}
			{isActive && !sort.descending ? (
				<ArrowUpOutlined
					className="report-stream__sort-icon"
					aria-hidden="true"
				/>
			) : (
				<ArrowDownOutlined
					className="report-stream__sort-icon"
					aria-hidden="true"
				/>
			)}
			<span className="sr-only">{`, ${state}`}</span>
		</button>
	);
}

// Grey bars where the rows will be, while the reports load.
function PlaceholderRows<T>({
	columns,
	loadingText,
}: Readonly<{
	columns: readonly ReportStreamColumn<T>[];
	loadingText: string;
}>) {
	return (
		<>
			<p className="sr-only">{loadingText}</p>
			<ul className="report-stream__rows" aria-hidden="true">
				{PLACEHOLDER_WIDTHS.map((width, rowIndex) => (
					<li key={width} className="report-stream__row">
						{columns.map((column, columnIndex) => (
							<div
								key={column.key}
								className={`report-stream__cell report-stream__cell--${column.narrow}`}
							>
								<span
									className="report-stream__placeholder"
									style={{
										width: `${PLACEHOLDER_WIDTHS[(rowIndex + columnIndex) % PLACEHOLDER_WIDTHS.length]}%`,
									}}
								/>
							</div>
						))}
					</li>
				))}
			</ul>
		</>
	);
}

// A stream's list of reports, one row per report, its columns from the
// stream's type. Each row is one link (the number's, stretched over it), with
// the row's ⋯ menu above it. Below about 1,000px of width the rows stack and
// the sort buttons stay above them (reportStream.scss).
function ReportStreamList<T>({
	label,
	columns,
	rows,
	getRowKey,
	renderRowMenu,
	sort,
	onSortChange,
	isLoading,
	loadingText,
	message,
	pagination,
}: Readonly<ReportStreamListProps<T>>) {
	const listRef = useRef<HTMLElement>(null);
	const hasSort = columns.some((column) => column.sortKey);
	const gridStyle = {
		"--report-stream-columns": `${columns.map((column) => column.width).join(" ")} 40px`,
	} as CSSProperties;

	const changePage = (page: number, pageSize: number) => {
		if (pageSize === pagination.pageSize) pagination.onPageChange(page);
		else pagination.onPageSizeChange(pageSize);
		listRef.current?.scrollIntoView({ block: "start" });
	};

	let body = message;
	if (isLoading) {
		body = <PlaceholderRows columns={columns} loadingText={loadingText} />;
	} else if (!message) {
		body = (
			<ul className="report-stream__rows">
				{rows.map((row) => (
					<li key={getRowKey(row)} className="report-stream__row">
						{columns.map((column) => (
							<div
								key={column.key}
								className={`report-stream__cell report-stream__cell--${column.narrow}`}
							>
								<span className="report-stream__cell-label">
									{column.header}
								</span>
								{column.render(row)}
							</div>
						))}
						<div className="report-stream__cell report-stream__cell--actions">
							{renderRowMenu(row)}
						</div>
					</li>
				))}
			</ul>
		);
	}

	return (
		<section
			ref={listRef}
			className="report-stream__list"
			aria-label={label}
			aria-busy={isLoading}
			style={gridStyle}
		>
			{/* Once the rows stack, only the sort buttons stay, after "Sort by". */}
			<div
				className={
					hasSort
						? "report-stream__head-row report-stream__head-row--sortable"
						: "report-stream__head-row"
				}
			>
				{hasSort && <span className="report-stream__sort-label">Sort by</span>}
				{columns.map((column) =>
					column.sortKey ? (
						<SortButton
							key={column.key}
							header={column.header}
							sortKey={column.sortKey}
							sort={sort}
							onSortChange={onSortChange}
						/>
					) : (
						// Each cell names its column for screen readers.
						<span key={column.key} aria-hidden="true">
							{column.header}
						</span>
					)
				)}
			</div>
			{body}
			{!isLoading && !message && (
				<div className="report-stream__footer">
					<span>{`Showing ${pagination.from}–${pagination.to} of ${pagination.total}`}</span>
					<Pagination
						current={pagination.page}
						pageSize={pagination.pageSize}
						total={pagination.total}
						showSizeChanger
						pageSizeOptions={STREAM_PAGE_SIZES}
						onChange={changePage}
					/>
				</div>
			)}
		</section>
	);
}

export default ReportStreamList;
